import React, { useState, useEffect, useRef } from 'react';
import { Camera } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const DEFAULT_CREST = '/petechtor-shield.png';
const CENTER_LOGO_STORAGE_KEY = 'petechtor_clock_center_logo';
const CENTER_PHOTO_SIZE = 240;
const HAND_PHOTO_SIZE = 160;
const ACCEPTED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'];

const isAcceptableImageFile = (file) => {
  if (file.type && file.type.startsWith('image/')) return true;
  const name = (file.name || '').toLowerCase();
  return ACCEPTED_IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const resizeImageToSquareDataUrl = (file, size) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Could not decode image'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        const scale = Math.max(size / img.width, size / img.height);
        const drawWidth = img.width * scale;
        const drawHeight = img.height * scale;
        ctx.drawImage(img, (size - drawWidth) / 2, (size - drawHeight) / 2, drawWidth, drawHeight);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
};

const HANDS = [
  {
    key: 'hour',
    storageKey: 'petechtor_clock_hour_photo',
    length: 58,
    color: '#f97316',
    defaultPhoto: 'https://images.unsplash.com/photo-1558788353-f76d92427f16?w=120&h=120&fit=crop&q=85'
  },
  {
    key: 'minute',
    storageKey: 'petechtor_clock_minute_photo',
    length: 82,
    color: '#3b82f6',
    defaultPhoto: 'https://images.unsplash.com/photo-1574158622682-e40e69881006?w=120&h=120&fit=crop&q=85'
  },
  {
    key: 'second',
    storageKey: 'petechtor_clock_second_photo',
    length: 104,
    color: '#22c55e',
    defaultPhoto: 'https://images.unsplash.com/photo-1609151354448-c4a53450c6e9?w=120&h=120&fit=crop&q=85'
  }
];

const ROMAN = ['XII', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI'];

export const PetClock = () => {
  const { t } = useLanguage();
  const [now, setNow] = useState(new Date());
  const [photos, setPhotos] = useState({});
  const [centerLogo, setCenterLogo] = useState(null);
  const fileInputRefs = useRef({});
  const centerLogoInputRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loaded = {};
    HANDS.forEach((h) => {
      const saved = localStorage.getItem(h.storageKey);
      if (saved) loaded[h.key] = saved;
    });
    setPhotos(loaded);

    const savedCenterLogo = localStorage.getItem(CENTER_LOGO_STORAGE_KEY);
    if (savedCenterLogo) setCenterLogo(savedCenterLogo);
  }, []);

  const handleCenterLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !isAcceptableImageFile(file)) return;
    try {
      const url = await resizeImageToSquareDataUrl(file, CENTER_PHOTO_SIZE);
      setCenterLogo(url);
      try {
        localStorage.setItem(CENTER_LOGO_STORAGE_KEY, url);
      } catch (err) {
        // Storage full/unavailable - photo is already shown via setCenterLogo above,
        // it just won't persist across a reload.
      }
    } catch (err) {
      // Could not read/decode this file - nothing to show
    }
  };

  const handleUseDefaultCrest = () => {
    localStorage.removeItem(CENTER_LOGO_STORAGE_KEY);
    setCenterLogo(null);
  };

  const handleFileChange = (handKey, storageKey) => async (e) => {
    const file = e.target.files[0];
    if (!file || !isAcceptableImageFile(file)) return;
    try {
      const url = await resizeImageToSquareDataUrl(file, HAND_PHOTO_SIZE);
      setPhotos((prev) => ({ ...prev, [handKey]: url }));
      try {
        localStorage.setItem(storageKey, url);
      } catch (err) {
        // Storage full/unavailable - photo is already shown via setPhotos above,
        // it just won't persist across a reload.
      }
    } catch (err) {
      // Could not read/decode this file - nothing to show
    }
  };

  const seconds = now.getSeconds();
  const minutes = now.getMinutes();
  const hours = now.getHours();

  const angles = {
    second: seconds * 6,
    minute: minutes * 6 + seconds * 0.1,
    hour: (hours % 12) * 30 + minutes * 0.5
  };

  return (
    <div className="rounded-2xl border border-slate-700/50 bg-slate-900/60 p-6 shadow-2xl text-center" data-testid="pet-clock-card">
      <div className="relative w-[min(16rem,90vw)] h-[min(16rem,90vw)] sm:w-72 sm:h-72 mx-auto">
        {/* Ambient glow - gives the clock a living, illuminated feel */}
        <div
          className="absolute -inset-6 rounded-full bg-gradient-to-br from-fuchsia-500/30 via-sky-400/25 to-emerald-400/25 blur-2xl animate-pulse pointer-events-none"
          data-testid="pet-clock-glow"
        />

        <div
          className="relative w-full h-full rounded-full border border-slate-700 bg-gradient-to-br from-slate-950 to-slate-900 shadow-inner"
          data-testid="pet-clock-face"
        >
        {/* Roman numerals */}
        {ROMAN.map((numeral, i) => {
          const angleRad = (i * 30 * Math.PI) / 180;
          const xPercent = 50 + 44 * Math.sin(angleRad);
          const yPercent = 50 - 44 * Math.cos(angleRad);
          return (
            <span
              key={numeral}
              className="absolute text-[11px] font-semibold text-slate-300"
              style={{ left: `${xPercent}%`, top: `${yPercent}%`, transform: 'translate(-50%, -50%)' }}
            >
              {numeral}
            </span>
          );
        })}

        {/* Center hub (shield crest) - click to upload a custom emblem */}
        <button
          type="button"
          onClick={() => centerLogoInputRef.current?.click()}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 z-0 rounded-full overflow-hidden group"
          data-testid="pet-clock-center-logo-upload-btn"
          title={t('clockCenterLogoTooltip')}
        >
          <img src={centerLogo || DEFAULT_CREST} alt="Petechtor" className="w-full h-full object-cover object-center" />
          <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-full transition-opacity">
            <Camera className="w-3.5 h-3.5 text-white" />
          </span>
        </button>
        <input
          ref={centerLogoInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
          onChange={handleCenterLogoChange}
          className="hidden"
          data-testid="pet-clock-center-logo-file-input"
        />

        {/* Wordmark below hub */}
        <div
          className="absolute left-1/2 text-[9px] font-extrabold tracking-wide whitespace-nowrap z-20"
          style={{ top: 'calc(50% + 44px)', transform: 'translateX(-50%)' }}
          data-testid="pet-clock-wordmark"
        >
          <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
            PETECHTOR
          </span>
        </div>

        {/* Hands with uploadable pet-photo tips */}
        {HANDS.map((hand) => {
          const angleDeg = angles[hand.key];
          const photo = photos[hand.key] || hand.defaultPhoto;
          return (
            <div
              key={hand.key}
              className="absolute top-1/2 left-1/2 origin-left z-20 pointer-events-none"
              style={{
                height: '2px',
                width: `${hand.length}px`,
                backgroundColor: hand.color,
                transform: `rotate(${angleDeg - 90}deg)`
              }}
            >
              <button
                type="button"
                onClick={() => fileInputRefs.current[hand.key]?.click()}
                className="absolute rounded-full overflow-hidden border-2 shadow-lg group pointer-events-auto"
                style={{
                  width: 28,
                  height: 28,
                  right: -14,
                  top: '50%',
                  transform: `translateY(-50%) rotate(${-(angleDeg - 90)}deg)`,
                  borderColor: hand.color
                }}
                data-testid={`pet-clock-${hand.key}-upload-btn`}
                title={t('clockHandUploadTooltip')}
              >
                <img src={photo} alt={`${hand.key} hand pet`} className="w-full h-full object-cover" />
                <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <Camera className="w-3 h-3 text-white" />
                </span>
              </button>
              <input
                ref={(el) => (fileInputRefs.current[hand.key] = el)}
                type="file"
                accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                onChange={handleFileChange(hand.key, hand.storageKey)}
                className="hidden"
                data-testid={`pet-clock-${hand.key}-file-input`}
              />
            </div>
          );
        })}
        </div>
      </div>

      {/* Digital readout, matching reference layout */}
      <p className="mt-5 text-sm text-slate-400">{t('clockCaption')}</p>
      <p className="text-2xl font-bold text-white tracking-wide" data-testid="pet-clock-digital-time">
        {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </p>
      <p className="text-xs text-slate-500 mb-3">{now.toLocaleDateString()}</p>
      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{t('clockUploadHint')}</p>

      {/* Center Logo Panel - matching reference site's upload/reset toggle */}
      <div className="flex items-center justify-center gap-2 mt-4">
        <button
          type="button"
          onClick={() => centerLogoInputRef.current?.click()}
          data-testid="pet-clock-upload-logo-btn"
          className="px-3 py-1.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-slate-800/70 border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors"
        >
          {t('clockUploadLogoBtn')}
        </button>
        <button
          type="button"
          onClick={handleUseDefaultCrest}
          disabled={!centerLogo}
          data-testid="pet-clock-use-default-crest-btn"
          className="px-3 py-1.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-slate-800/70 border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {t('clockUseDefaultCrestBtn')}
        </button>
      </div>
    </div>
  );
};
